import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Select, Dropdown, Space } from 'antd';
import { DownOutlined, PlusOutlined, UploadOutlined, DownloadOutlined, SettingOutlined, EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { ThemeSwitcher } from '../../../../../components/ThemeSwitcher';
import { LanguageSwitcher } from '../../../../../components/LanguageSwitcher';
import BackToTools from '../../../components/BackToTools';
import styles from './index.module.scss';

const { Option } = Select;

const Toolbar = ({
  showAddBar,
  axisName,
  axisGridDensity,
  nodeStyle,
  onChangeAxisGridDensity,
  onChangeNodeStyle,
  onRenameAxis,
  onToggleAddBar,
  onAddNode,
  onExportJSON,
  onImportJSON,
  onExportSVG,
  onExportPNG,
  onOpenLaneManager,
  onOpenAxisEditor,
  lanes,
  laneOrder,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [editingAxisName, setEditingAxisName] = useState(false);
  const [axisNameDraft, setAxisNameDraft] = useState(axisName || t('tools.timeline.title'));

  useEffect(() => {
    setAxisNameDraft(axisName || t('tools.timeline.title'));
  }, [axisName, t]);

  const [newNode, setNewNode] = useState({
    label: '',
    date: new Date().toISOString().slice(0, 10),
    cat: laneOrder[0] || 'sys',
    priority: 'P1',
  });

  const handleAdd = () => {
    if (!newNode.label.trim()) {
      return;
    }
    onAddNode(newNode);
    setNewNode({
      label: '',
      date: new Date().toISOString().slice(0, 10),
      cat: laneOrder[0] || 'sys',
      priority: 'P1',
    });
  };

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onImportJSON(file);
      e.target.value = '';
    }
  };

  const dataMenuItems = [
    {
      key: 'import',
      label: t('tools.timeline.importJSON'),
      icon: <UploadOutlined />,
      onClick: () => fileInputRef.current?.click(),
    },
    {
      key: 'export',
      label: t('tools.timeline.exportJSON'),
      icon: <DownloadOutlined />,
      onClick: onExportJSON,
    },
    { type: 'divider' },
    {
      key: 'exportSVG',
      label: t('tools.timeline.exportSVG'),
      onClick: onExportSVG,
    },
    {
      key: 'exportPNG',
      label: t('tools.timeline.exportPNG'),
      onClick: onExportPNG,
    },
  ];

  const editMenuItems = [
    {
      key: 'lanes',
      label: t('tools.timeline.laneManagement'),
      icon: <SettingOutlined />,
      onClick: onOpenLaneManager,
    },
    {
      key: 'axis',
      label: t('tools.timeline.axisManagement'),
      icon: <SettingOutlined />,
      onClick: onOpenAxisEditor,
    },
  ];

  const saveAxisName = () => {
    const next = (axisNameDraft || '').trim();
    if (!next) {
      return;
    }
    if (onRenameAxis) {
      onRenameAxis(next);
    }
    setEditingAxisName(false);
  };

  return (
    <div className={styles.toolbar}>
      <div className={styles.toolbarHeader}>
        <div className={styles.titleSection}>
          {editingAxisName ? (
            <Space size="small" align="center">
              <Input
                value={axisNameDraft}
                onChange={(e) => setAxisNameDraft(e.target.value)}
                size="small"
                style={{ width: 220 }}
                onPressEnter={saveAxisName}
              />
              <Button size="small" type="primary" icon={<CheckOutlined />} onClick={saveAxisName} />
              <Button
                size="small"
                icon={<CloseOutlined />}
                onClick={() => {
                  setAxisNameDraft(axisName || t('tools.timeline.title'));
                  setEditingAxisName(false);
                }}
              />
            </Space>
          ) : (
            <Space size="small" align="center">
              <h1 className={styles.title}>{axisName || t('tools.timeline.title')}</h1>
              <Button size="small" icon={<EditOutlined />} onClick={() => setEditingAxisName(true)} />
            </Space>
          )}
          <div className={styles.legend}>
            <span className={styles.legendItem}>
              <span className={styles.dot} style={{ background: '#ef4444' }} />
              {t('tools.timeline.p0')}
            </span>
            <span className={styles.legendItem}>
              <span className={styles.dot} style={{ background: '#f59e0b' }} />
              {t('tools.timeline.p1')}
            </span>
            <span className={styles.legendItem}>
              <span className={styles.dot} style={{ background: '#6b7280' }} />
              {t('tools.timeline.p2')}
            </span>
            <span className={styles.legendItem}>
              <span className={styles.dot} style={{ background: '#d1d5db' }} />
              {t('tools.timeline.p3')}
            </span>
          </div>
        </div>

        <Space wrap>
          <Select
            value={axisGridDensity || 'auto'}
            size="small"
            style={{ width: 130 }}
            onChange={onChangeAxisGridDensity}
            aria-label={t('tools.timeline.gridDensity')}
          >
            <Option value="auto">{t('tools.timeline.gridAuto')}</Option>
            <Option value="month">{t('tools.timeline.gridMonth')}</Option>
            <Option value="week">{t('tools.timeline.gridWeek')}</Option>
            <Option value="day">{t('tools.timeline.gridDay')}</Option>
          </Select>

          <Select
            value={nodeStyle || 'tag'}
            size="small"
            style={{ width: 140 }}
            onChange={onChangeNodeStyle}
            aria-label={t('tools.timeline.nodeStyle')}
          >
            <Option value="tag">{t('tools.timeline.nodeStyleTag')}</Option>
            <Option value="fish">{t('tools.timeline.nodeStyleFish')}</Option>
            <Option value="rocket">{t('tools.timeline.nodeStyleRocket')}</Option>
            <Option value="leaf">{t('tools.timeline.nodeStyleLeaf')}</Option>
          </Select>

          <Dropdown menu={{ items: dataMenuItems }} trigger={['click']}>
            <Button>
              {t('tools.timeline.data')} <DownOutlined />
            </Button>
          </Dropdown>

          <Dropdown menu={{ items: editMenuItems }} trigger={['click']}>
            <Button>
              {t('tools.timeline.edit')} <DownOutlined />
            </Button>
          </Dropdown>

          <Button type="primary" icon={<PlusOutlined />} onClick={onToggleAddBar}>
            {t('tools.timeline.addNode')}
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            style={{ display: 'none' }}
            onChange={handleImport}
          />

          <div className={styles.globalControls}>
            <BackToTools />
            <ThemeSwitcher />
            <LanguageSwitcher />
          </div>
        </Space>
      </div>

      {showAddBar && (
        <div className={styles.addBar}>
          <Input
            placeholder={t('tools.timeline.nodeName')}
            value={newNode.label}
            onChange={(e) => setNewNode({ ...newNode, label: e.target.value })}
            style={{ flex: 2, minWidth: 160 }}
          />
          <Input
            type="date"
            value={newNode.date}
            onChange={(e) => setNewNode({ ...newNode, date: e.target.value })}
            style={{ flex: 1, minWidth: 130 }}
          />
          <Select
            value={newNode.cat}
            onChange={(cat) => setNewNode({ ...newNode, cat })}
            style={{ flex: 1, minWidth: 160 }}
          >
            {laneOrder.map((key) => (
              <Option key={key} value={key}>
                {lanes[key]?.name}
              </Option>
            ))}
          </Select>
          <Select
            value={newNode.priority}
            onChange={(priority) => setNewNode({ ...newNode, priority })}
            style={{ flex: 'none', minWidth: 90 }}
          >
            <Option value="P0">P0</Option>
            <Option value="P1">P1</Option>
            <Option value="P2">P2</Option>
            <Option value="P3">P3</Option>
          </Select>
          <Space>
            <Button type="primary" size="small" onClick={handleAdd}>
              {t('tools.timeline.add')}
            </Button>
            <Button size="small" onClick={onToggleAddBar}>
              {t('tools.timeline.cancel')}
            </Button>
          </Space>
        </div>
      )}
    </div>
  );
};

export default Toolbar;
